/**
 * Shared export helpers used by both the dashboard Export dropdown and the
 * "Download Report" action on the pipeline loading screen.
 */

export function generateCareerImpactMarkdown(data: any): string {
  const { projectReview, repository, analysis: meta, intelligenceReport } = data || {};
  const cq = projectReview?.codeQuality || {};
  const emp = projectReview?.employabilitySignal || {};
  const auth = projectReview?.projectAuthenticity || {};
  const strengths = projectReview?.strengths || [];
  const weaknesses = projectReview?.weaknesses || [];
  const resumeBullets = intelligenceReport?.resumeBullets || [];
  const questions = data?.interviewSimulation?.questions || [];
  const repoName = repository?.repositoryName || meta?.repositoryName || meta?.repositoryUrl || 'Repository';

  const currentScore = emp.overall ?? 0;
  const availableHeadroom = 100 - currentScore;
  const pointsMap: Record<string, number> = { high: 15, medium: 10, low: 5 };
  const quickWins = [...weaknesses]
    .sort((a: any, b: any) => {
      const sv = (s?: string) => s === 'high' ? 3 : s === 'medium' ? 2 : 1;
      return sv(b.severity) - sv(a.severity);
    })
    .slice(0, 3);
  const totalBasePoints = quickWins.reduce((acc, w) => acc + (pointsMap[w.severity] ?? 8), 0);
  const realismFactor = totalBasePoints > 0 ? Math.min(1, (availableHeadroom * 0.8) / totalBasePoints) : 1;

  let md = '';
  md += `# Career Impact Report - ${repoName}\n\n`;
  md += `> Generated by DevContext.AI on ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}\n\n`;

  if (meta?.repositoryUrl) md += `**Repository:** ${meta.repositoryUrl}\n\n`;

  md += `## Scores Overview\n\n`;
  md += `| Metric | Score |\n|---|---|\n`;
  md += `| **Employability Signal** | ${emp.overall ?? '-'}/100 |\n`;
  md += `| **Code Quality** | ${cq.overall ?? '-'}/100 |\n`;
  md += `| **Authenticity** | ${auth.score ?? '-'}/100 |\n\n`;

  if (emp.companyTierMatch) {
    md += `## Company Tier Match\n\n`;
    md += `| Tier | Score |\n|---|---|\n`;
    md += `| Big Tech / FAANG | ${emp.companyTierMatch.bigTech ?? '-'} |\n`;
    md += `| Product Companies | ${emp.companyTierMatch.productCompanies ?? '-'} |\n`;
    md += `| Startups | ${emp.companyTierMatch.startups ?? '-'} |\n`;
    md += `| Service Companies | ${emp.companyTierMatch.serviceCompanies ?? '-'} |\n\n`;
  }

  md += `## Code Quality Breakdown\n\n`;
  md += `| Category | Score |\n|---|---|\n`;
  for (const key of ['readability', 'maintainability', 'errorHandling', 'security', 'performance', 'documentation']) {
    if (cq[key] !== undefined) {
      const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, (s: string) => s.toUpperCase());
      md += `| ${label} | ${cq[key]}/100 |\n`;
    }
  }
  md += `\n`;

  if (repository?.frameworks?.length) {
    md += `## Tech Stack\n\n`;
    md += repository.frameworks.map((f: string) => `- ${f}`).join('\n') + '\n\n';
  }

  if (quickWins.length > 0) {
    md += `## Quick Wins\n\n`;
    quickWins.forEach((w: any, i: number) => {
      const basePts = pointsMap[w.severity] ?? 8;
      const pts = Math.max(1, Math.floor(basePts * realismFactor));
      md += `${i + 1}. **${w.issue}** (+${pts} pts)\n`;
      if (w.recommendation) md += `   - Fix: ${w.recommendation}\n`;
      md += `\n`;
    });
  }

  if (strengths.length > 0) {
    md += `## Strengths (${strengths.length})\n\n`;
    strengths.forEach((s: any) => {
      md += `- **${s.pattern}** - ${s.description}\n`;
      if (s.impact) md += `  - Impact: ${s.impact}\n`;
    });
    md += `\n`;
  }

  if (weaknesses.length > 0) {
    md += `## Areas for Improvement (${weaknesses.length})\n\n`;
    weaknesses.forEach((w: any) => {
      md += `- **${w.issue}** [${w.severity || 'medium'}]\n`;
      if (w.impact) md += `  - Impact: ${w.impact}\n`;
      if (w.recommendation) md += `  - Fix: ${w.recommendation}\n`;
    });
    md += `\n`;
  }

  if (resumeBullets.length > 0) {
    md += `## Resume Bullets (${resumeBullets.length})\n\n`;
    resumeBullets.forEach((b: any) => {
      md += `- ${b.bullet || b.text || JSON.stringify(b)}\n`;
    });
    md += `\n`;
  }

  if (questions.length > 0) {
    md += `## Comprehensive Question Sheet (${questions.length} Questions)\n\n`;
    questions.forEach((q: any, i: number) => {
      md += `### Q${i + 1}: ${q.question}\n`;
      md += `- Category: ${q.category} | Difficulty: ${q.difficulty}\n\n`;
    });
  }

  md += `---\n* This report is AI-generated and may contain mistakes. Validate important claims.\n`;
  return md;
}

export function downloadTextFile(content: string, filename: string, mime: string) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function downloadPdfViaPrint() {
  window.print();
}

function escapeHtml(s: string) {
  return String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

export function buildQuestionSheetHtml(data: any): { title: string; html: string } {
  const repoName =
    data?.repository?.repositoryName ||
    data?.analysis?.repositoryName ||
    data?.analysis?.repositoryUrl ||
    'Interview Question Sheet';
  const questions: any[] = data?.interviewSimulation?.questions || data?.interviewQuestions || [];
  const createdAt = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  const title = `${repoName} - Question Sheet`;

  const rows = questions.map((q, idx) => {
    const category = q?.category ? String(q.category) : '';
    const difficulty = q?.difficulty ? String(q.difficulty) : '';
    const meta = [category, difficulty].filter(Boolean).join(' | ');
    return `
      <div class="q">
        <div class="q-head">
          <div class="q-num">Q${idx + 1}</div>
          <div class="q-meta">${escapeHtml(meta)}</div>
        </div>
        <div class="q-text">${escapeHtml(q?.question || '')}</div>
      </div>
    `;
  }).join('\n');

  const html = `<!doctype html>
  <html>
    <head>
      <meta charset="utf-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <title>${escapeHtml(title)}</title>
      <style>
        :root { --ink:#111; --muted:#555; --border:#e6e6e6; }
        * { box-sizing: border-box; }
        body { margin: 0; font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial; color: var(--ink); }
        .page { max-width: 900px; margin: 0 auto; padding: 32px 28px 40px; }
        .title { font-size: 22px; font-weight: 800; margin: 0 0 8px; }
        .sub { color: var(--muted); font-size: 12px; line-height: 1.6; margin-bottom: 18px; }
        .pill { display:inline-block; border:1px solid var(--border); border-radius: 999px; padding: 3px 10px; font-size: 11px; margin-right: 8px; }
        .q { border: 1px solid var(--border); border-radius: 12px; padding: 14px 14px 12px; margin: 10px 0; page-break-inside: avoid; }
        .q-head { display:flex; justify-content: space-between; align-items: baseline; gap: 10px; margin-bottom: 8px; }
        .q-num { font-weight: 800; font-size: 12px; }
        .q-meta { font-size: 11px; color: var(--muted); }
        .q-text { font-size: 13.5px; line-height: 1.5; white-space: pre-wrap; }
        .footer { margin-top: 20px; font-size: 11px; color: var(--muted); border-top:1px solid var(--border); padding-top: 12px; }
        @media print {
          .page { max-width: none; padding: 18mm 14mm; }
          .q { border-color: #ddd; }
        }
      </style>
    </head>
    <body>
      <div class="page">
        <h1 class="title">${escapeHtml(repoName)}</h1>
        <div class="sub">
          <span class="pill">${escapeHtml(String(questions.length))} questions</span>
          <span class="pill">Generated ${escapeHtml(createdAt)}</span>
          <div style="margin-top:10px">* AI-generated content may contain mistakes. Validate before using in interviews.</div>
        </div>
        ${rows || '<div class="sub">No questions found.</div>'}
        <div class="footer">DevContext.AI - Question Sheet</div>
      </div>
    </body>
  </html>`;

  return { title, html };
}

export function renderAndPrintQuestionSheet(win: Window, data: any) {
  const { title, html } = buildQuestionSheetHtml(data);
  try {
    win.document.open();
    win.document.write(html);
    win.document.close();
    win.document.title = title;
    win.focus();
    // Give layout a moment before opening the print dialog.
    setTimeout(() => {
      try {
        win.print();
        // Close after print in browsers that support it.
        (win as any).onafterprint = () => {
          try { win.close(); } catch {}
        };
      } catch {
        // ignore
      }
    }, 250);
  } catch {
    // ignore
  }
}
